import { useState, useCallback, useEffect } from 'react';
import {
  Reservation,
  ReservationActivity,
  reservationService,
  ReservationChangeOptionsResponse,
} from '@lib/state';
import { isStartOfDayLockError } from './isStartOfDayLockError';

export { isStartOfDayLockError };

interface Options {
  reservation?: Reservation;
  activityType?: ReservationActivity;
  onAllowed?: () => void;
}

export function useGetReservationChangeOptions({
  reservation,
  activityType,
  onAllowed,
}: Options = {}) {
  const [isLocked, setIsLocked] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [changeOptions, setChangeOptions] = useState<ReservationChangeOptionsResponse | null>(null);
  const [error, setError] = useState<unknown>(null);

  const refetch = useCallback(
    async (reservation: Reservation, activityType: ReservationActivity, onAllowed?: () => void) => {
      setIsFetching(true);
      setError(null);
      try {
        // Call backend preflight and inspect explicit SOD lock status when available.
        const options = await reservationService.GetReservationChangeOptions(
          reservation,
          activityType
        );
        setChangeOptions(options);

        if (options.startOfDayLock?.isLocked) {
          setIsLocked(true);
          return;
        }

        setIsLocked(false);
        // If no error is thrown, the check passed; proceed with the action
        if (onAllowed) onAllowed();
      } catch (error) {
        // If it's a Start of Day lock error, show the blocking dialog
        if (isStartOfDayLockError(error)) {
          setIsLocked(true);
        } else {
          // For other errors, clear the lock state so callers don't misreport
          // the cause as a SOD lock, then surface via error state.
          setIsLocked(false);
          setError(error);
        }
      } finally {
        setIsFetching(false);
      }
    },
    []
  );

  useEffect(() => {
    if (reservation && activityType) {
      refetch(reservation, activityType, onAllowed ?? (() => {}));
    }
  }, [reservation?.id, activityType]);

  return {
    isLocked,
    lockReason: changeOptions?.startOfDayLock?.reason,
    changeOptions,
    refetch,
    isFetching,
    error,
  };
}
